import { ExternalLink, FileText } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedPoliceReportStatusChangedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface PoliceReportStatusChangedEventProps {
  event: DiscriminatedPoliceReportStatusChangedEvent;
  matterId: string;
}

export const PoliceReportStatusChangedEvent: React.FC<
  PoliceReportStatusChangedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { police_report, old_report_status, new_report_status } = payload;

  // Get department name
  const departmentName =
    police_report.police_department?.name || 'Unknown Department';

  // Get report identifier
  const reportIdentifier = police_report.report_identifier;

  // Format statuses
  const oldStatusLabel = formatAttestationStatus(old_report_status);
  const newStatusLabel = formatAttestationStatus(new_report_status);

  // Determine variant for new status
  const newStatusVariant = getStatusVariant(new_report_status);

  return (
    <EventContainer>
      <EventHeader
        icon={<FileText size={20} />}
        title="Police Report Status Changed"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Department Name */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{departmentName}</span>
          </div>

          {/* Report Identifier */}
          {reportIdentifier && (
            <div className="text-xs">
              <span className="text-slate-600">Report #:</span>{' '}
              <span className="font-mono text-[11px]">{reportIdentifier}</span>
            </div>
          )}

          {/* Status Change */}
          <div className="flex items-center gap-2 text-xs mt-2">
            <span className="text-slate-600">Status:</span>
            <Badge variant="outline" className="line-through">
              {oldStatusLabel}
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge variant={newStatusVariant}>{newStatusLabel}</Badge>
          </div>

          {/* Attestation Description */}
          {police_report.attestation_description && (
            <div className="text-xs text-slate-600 italic">
              {police_report.attestation_description}
            </div>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/police-reports/${police_report.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Report
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions
function formatAttestationStatus(status: string): string {
  const statuses: Record<string, string> = {
    rejected_verified: 'Rejected (Verified)',
    unclear: 'Unclear',
    inferred: 'Inferred',
    attested_unverified: 'Attested (Unverified)',
    attested_verified: 'Attested (Verified)',
    verified_received: 'Received',
  };
  return statuses[status] || status;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'verified_received':
      return 'default'; // Green for received
    case 'attested_verified':
      return 'secondary'; // Blue-ish for verified
    case 'rejected_verified':
      return 'destructive'; // Red for rejected
    case 'attested_unverified':
    case 'inferred':
    case 'unclear':
    default:
      return 'outline'; // Gray for others
  }
}
